import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Chip,
  Drawer, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemIcon, 
  ListItemText, 
  Menu,
  MenuItem,
  Toolbar, 
  Typography,
  Divider,
  Container,
  Button,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon, 
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  AccountBox as AccountBoxIcon,
  Logout as LogoutIcon,
  Radar as RadarIcon,
  Webhook as WebhookIcon,
  SwapHoriz as SwapHorizIcon,
  Bolt as BoltIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  SmartToy as AIInsightsIcon,
  EditNote as PaperIcon,
  History as HistoryIcon,
  AccountCircle as AccountCircleIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AttachMoney as AttachMoneyIcon,
  ReceiptLong as ReceiptLongIcon,
  RequestQuote as TaxIcon,
  Star as WatchlistIcon,
  AccountTree as StrategyIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Badge } from '@mui/material';
import SettingsMenu from './SettingsMenu';
import MaintenanceMenu from './MaintenanceMenu';
import WatchlistBox from './WatchlistBox';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationProvider';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';
import { usePaperMode } from '../contexts/PaperModeContext';
import { useThemeMode } from '../contexts/ThemeContext';
import AIInsightsPanel from './AIInsightsPanel';
import QuickTradeDrawer from './QuickTradeDrawer';
import CustomReminderDialog from './CustomReminderDialog';
import MarketTicker from './MarketTicker';

// Drawer width becomes resizable; persisted in localStorage
const MIN_DRAWER_WIDTH = 200;
const MAX_DRAWER_WIDTH = 600;
const COLLAPSED_DRAWER_WIDTH = 72;

// Watchlist column defaults must match WatchlistBox's colWidths defaults.
// Overhead covers cell padding (px:0.25 * 5 cols), card padding (p:1) and borders.
const WATCHLIST_COL_DEFAULTS = { symbol: 72, last: 55, change: 45, change_percentage: 60, action: 36 };
const WATCHLIST_OVERHEAD_PX = 42;

const getWatchlistWidth = () => {
  let saved = {};
  try { saved = JSON.parse(localStorage.getItem('watchlist_col_widths_v2') || '{}'); } catch {}
  const total = Object.keys(WATCHLIST_COL_DEFAULTS)
    .reduce((sum, k) => sum + (Number(saved?.[k]) || WATCHLIST_COL_DEFAULTS[k]), 0);
  return total + WATCHLIST_OVERHEAD_PX;
};

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { privacyMask, togglePrivacyMask } = usePrivacyMask();
  const { paperMode, togglePaperMode } = usePaperMode();
  const { mode, toggleMode } = useThemeMode();
  const { unreadCount, clearUnread } = useNotifications() || {};
  const [aiPanelOpen, setAiPanelOpen] = React.useState(false);
  const [quickTradeOpen, setQuickTradeOpen] = React.useState(() => localStorage.getItem('quickTradeOpen') === 'true');
  const [watchlistWidth, setWatchlistWidth] = React.useState(getWatchlistWidth);
  const minDrawerWidth = Math.max(MIN_DRAWER_WIDTH, watchlistWidth);
  const [drawerWidth, setDrawerWidth] = React.useState(() => {
    const saved = Number(localStorage.getItem('drawerWidth'));
    if (Number.isFinite(saved)) return Math.min(MAX_DRAWER_WIDTH, Math.max(minDrawerWidth, saved));
    return minDrawerWidth;
  });
  const [drawerCollapsed, setDrawerCollapsed] = React.useState(() => localStorage.getItem('drawerCollapsed') === 'true');
  const resizingRef = React.useRef(false);
  const effectiveDrawerWidth = drawerCollapsed ? COLLAPSED_DRAWER_WIDTH : drawerWidth;

  // Grow the drawer if watchlist columns are widened past the current width
  React.useEffect(() => {
    const onColResize = () => setWatchlistWidth(getWatchlistWidth());
    window.addEventListener('watchlist-col-resize', onColResize);
    return () => window.removeEventListener('watchlist-col-resize', onColResize);
  }, []);

  React.useEffect(() => {
    setDrawerWidth(w => Math.min(MAX_DRAWER_WIDTH, Math.max(minDrawerWidth, w)));
  }, [minDrawerWidth]);

  const toggleDrawerCollapsed = () => {
    setDrawerCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('drawerCollapsed', String(next)); } catch {}
      return next;
    });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  React.useEffect(() => () => {
    // Cleanup listeners on unmount
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', stopResize);
  }, []);

  // Section 1 — core trading views
  const menuItemsTop = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Position Summary', icon: <AssessmentIcon />, path: '/stock-summary' },
    { text: 'Chains', icon: <LinkIcon />, path: '/chains' },
    { text: 'Order History', icon: <HistoryIcon />, path: '/order-history' },
    { text: 'Webhook Alerts', icon: <WebhookIcon />, path: '/webhook-alerts' },
    { text: 'Stock Analysis', icon: <TrendingUpIcon />, path: '/stock-analysis' },
  ];
  // Section 2 — research & automation
  const menuItemsMid = [
    { text: 'Scanner', icon: <RadarIcon />, path: '/scanner' },
    { text: 'Watchlist', icon: <WatchlistIcon />, path: '/orders' },
    { text: 'Strategy Builder', icon: <StrategyIcon />, path: '/strategy-builder' },
  ];
  // Section 3 — account reporting (Accounts/User Approvals live under Settings)
  const menuItemsBottom = [
    { text: 'Account Balances', icon: <AttachMoneyIcon />, path: '/account-summary' },
    { text: 'Transactions', icon: <ReceiptLongIcon />, path: '/transactions' },
    { text: 'Tax Projection', icon: <TaxIcon />, path: '/tax-projection' },
  ].filter(item => {
    if (paperMode && (item.path === '/account-summary' || item.path === '/transactions')) return false;
    return true;
  });
  const allMenuItems = [...menuItemsTop, ...menuItemsMid, ...menuItemsBottom];

  const onMouseMove = (e) => {
    if (!resizingRef.current) return;
    const newWidth = Math.min(MAX_DRAWER_WIDTH, Math.max(minDrawerWidth, e.clientX));
    setDrawerWidth(newWidth);
  };

  const stopResize = () => {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', stopResize);
    try { localStorage.setItem('drawerWidth', String(drawerWidth)); } catch {}
    document.body.style.cursor = '';
  };

  const startResize = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopResize);
    document.body.style.cursor = 'col-resize';
  };


  const renderMenuItem = (item, isCollapsed) => (
    <Tooltip
      key={item.text}
      title={isCollapsed ? item.text : ''}
      placement="right"
      arrow
    >
      <ListItemButton
        component={Link}
        to={item.path}
        selected={location.pathname === item.path}
        sx={isCollapsed ? { justifyContent: 'center', px: 0.5 } : { px: 1 }}
      >
        <ListItemIcon sx={isCollapsed ? { minWidth: 0, justifyContent: 'center' } : { minWidth: 36 }}>
          {item.badge && item.badge > 0 ? (
            <Badge badgeContent={item.badge} color="error" max={99}>
              {item.icon}
            </Badge>
          ) : (
            item.icon
          )}
        </ListItemIcon>
        {!isCollapsed && <ListItemText primary={item.text} />}
      </ListItemButton>
    </Tooltip>
  );

  const drawerContent = (isCollapsed) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Toolbar
        sx={isCollapsed
          ? { justifyContent: 'center', px: 0 }
          : { px: 1, minHeight: '96px !important' }}
      >
        <Box
          component="img"
          src={isCollapsed
            ? (mode === 'dark' ? '/assets/mytradeshub-icon-dark.png' : '/assets/mytradeshub-icon.png')
            : (mode === 'dark' ? '/assets/mytradeshub-logo-dark.png' : '/assets/mytradeshub-logo.png')}
          alt="myTradesHub"
          sx={isCollapsed
            ? { width: 48, height: 48, objectFit: 'contain' }
            : { height: 80, maxWidth: '100%', objectFit: 'contain' }}
        />
      </Toolbar>
      <Divider />
      {/* Scrollable nav region */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
        <List>
          {menuItemsTop.map((item) => renderMenuItem(item, isCollapsed))}
        </List>
        <Divider />
        <List>
          {menuItemsMid.map((item) => renderMenuItem(item, isCollapsed))}
        </List>
        <Divider />
        <List>
          {menuItemsBottom.map((item) => renderMenuItem(item, isCollapsed))}
          <SettingsMenu collapsed={isCollapsed} onExpand={toggleDrawerCollapsed} />
          <MaintenanceMenu collapsed={isCollapsed} onExpand={toggleDrawerCollapsed} />
        </List>
        {/* Watchlist placed at the bottom of the scroll region */}
        <Box sx={{ mt: 'auto' }} />
        {!isCollapsed && (
          <Box sx={{ px: 1, pt: 1, pb: 1 }}>
            <WatchlistBox />
          </Box>
        )}
      </Box>
      {/* Collapse toggle — pinned footer, always visible */}
      <Divider />
      <Tooltip title={isCollapsed ? 'Expand' : ''} placement="right" arrow>
        <ListItemButton
          onClick={toggleDrawerCollapsed}
          sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start', py: 1, px: 1, flexShrink: 0 }}
        >
          <ListItemIcon sx={isCollapsed ? { minWidth: 0, justifyContent: 'center' } : { minWidth: 36 }}>
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </ListItemIcon>
          {!isCollapsed && <ListItemText primary="Collapse" />}
        </ListItemButton>
      </Tooltip>
      {/* Resize handle overlay at the right edge of the drawer */}
      {!isCollapsed && (
        <Box
          onMouseDown={startResize}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 6,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 10,
            '&:hover': { bgcolor: 'action.hover' }
          }}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        color={paperMode ? 'warning' : 'primary'}
        sx={{
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { sm: `${effectiveDrawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label={drawerCollapsed ? 'expand sidebar' : 'collapse sidebar'}
            edge="start"
            onClick={toggleDrawerCollapsed}
            title={drawerCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {drawerCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            {allMenuItems.find(item => item.path === location.pathname)?.text ||
             ({ '/field-mappings': 'Field Mappings',
                '/accounts': 'Accounts',
                '/admin/users': 'User Approvals',
                '/admin/api-usage': 'API Usage',
                '/maintenance': 'Other',
                '/recon-issues': 'Reconciliation Issues',
                '/sync-logs': 'Sync Logs',
                '/paper-trading': 'Paper Trading',
                '/api-tokens': 'Alert Config',
                '/user-profile': 'User Profile',
                '/quick-trade': 'Quick Trade',
                '/earnings-update': 'Earnings Update',
                '/robinhood-login': 'Robinhood Login',
                '/robinhood-positions': 'Robinhood Positions',
                '/tradier-mcp-test': 'Tradier MCP Test',
                '/option-order-test': 'Option Order Test',
                '/equity-order-test': 'Equity Order Test',
                '/robinhood-order-test': 'Robinhood Order Test',
                '/robinhood-feature-test': 'Robinhood Feature Test',
                '/import-data': 'Import Data',
                '/copy-trading': 'Copy Trading',
                '/notification-settings': 'Notification Settings',
              }[location.pathname] ||
             (location.pathname.startsWith('/stock-analysis') ? 'Stock Analysis' : 'MyTradesHub'))}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={paperMode ? 'PAPER' : 'LIVE'}
              color={paperMode ? 'warning' : 'success'}
              onClick={togglePaperMode}
              clickable
              size="small"
              sx={{ fontWeight: 'bold', color: '#fff', '& .MuiChip-label': { px: 1 } }}
            />
            <IconButton
              color="inherit"
              size="small"
              onClick={() => {
                const next = !quickTradeOpen;
                setQuickTradeOpen(next);
                localStorage.setItem('quickTradeOpen', String(next));
              }}
              aria-label="Quick Trade"
              title="Quick Trade"
            >
              <BoltIcon fontSize="medium" />
            </IconButton>
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setAiPanelOpen(true)}
              aria-label="AI Trade Insights"
              title="AI Trade Insights"
            >
              <AIInsightsIcon fontSize="medium" />
            </IconButton>
            <CustomReminderDialog />
            <IconButton
              color="inherit"
              size="small"
              onClick={togglePrivacyMask}
              aria-label={privacyMask ? 'Show financial amounts' : 'Mask financial amounts'}
              title={privacyMask ? 'Show financial amounts' : 'Mask financial amounts'}
            >
              {privacyMask ? <VisibilityOffIcon fontSize="medium" /> : <VisibilityIcon fontSize="medium" />}
            </IconButton>
            <IconButton
              color="inherit"
              size="small"
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              aria-label="User menu"
              title="User menu"
            >
              <AccountCircleIcon fontSize="medium" />
            </IconButton>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { navigate('/user-profile'); setUserMenuAnchor(null); }}>
                <AccountCircleIcon fontSize="medium" sx={{ mr: 1 }} /> User Profile
              </MenuItem>
              <MenuItem onClick={() => { toggleMode(); setUserMenuAnchor(null); }}>
                {mode === 'dark' ? <LightModeIcon fontSize="medium" sx={{ mr: 1 }} /> : <DarkModeIcon fontSize="medium" sx={{ mr: 1 }} />}
                {mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </MenuItem>
              <MenuItem onClick={() => { logout(); setUserMenuAnchor(null); }}>
                <LogoutIcon fontSize="medium" sx={{ mr: 1 }} /> Logoff
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
        {/* Market ticker strip — its own row under the app bar so it can never
            crowd the title or the toolbar icons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '34px',
            bgcolor: 'rgba(0,0,0,0.18)',
            borderTop: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <MarketTicker />
        </Box>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: effectiveDrawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* Mobile drawer — always expanded */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, position: 'fixed', height: '100vh', overflow: 'auto' },
          }}
        >
          {drawerContent(false)}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: effectiveDrawerWidth, position: 'fixed', height: '100vh', overflow: 'auto', transition: 'width 0.2s ease' },
          }}
          open
        >
          {drawerContent(drawerCollapsed)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, minWidth: 0, p: { xs: 0, sm: 1 }, width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` }, height: '100vh', overflowY: 'auto', overflowX: { xs: 'hidden', sm: 'auto' } }}
      >
        <Toolbar /> {/* This is for spacing below the AppBar */}
        <Box sx={{ height: '34px' }} /> {/* spacer for the fixed ticker strip */}
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 1 } }}>
          {children}
        </Container>
      </Box>
      <AIInsightsPanel
        open={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        selectedAccount={['all']}
      />
      <QuickTradeDrawer
        open={quickTradeOpen}
        onClose={() => {
          setQuickTradeOpen(false);
          localStorage.setItem('quickTradeOpen', 'false');
        }}
      />
    </Box>
  );
};

export default Layout;
